import { QueryClientContext } from '@tanstack/react-query';
import { useContext, useId, useState } from 'react';
import { Link } from 'react-router';

import { HELP_MAX, HelpTip, LEAD_MAX, warnLong } from './help.jsx';
import { ArrowDownIcon, ArrowLeftIcon, ArrowUpIcon } from './icons.jsx';
import { makeT, useT } from '../lib/i18n.js';

export { DetailList, Disclosure, FormGrid, HelpTip, InlineNotice, Toolbar } from './help.jsx';
export * from './icons.jsx';

/*
 * Small, plain building blocks. Tables for data, panels for forms; one radius scale
 * (6 px controls, 10 px panels) so hierarchy reads from structure, not decoration.
 */

const buttonStyles = {
  primary: 'bg-accent text-on-accent hover:bg-accent-hover',
  secondary: 'bg-surface text-text border border-line hover:border-muted',
  danger: 'bg-surface text-danger border border-danger/50 hover:bg-danger hover:text-white',
  ghost: 'text-muted hover:text-text',
};

/**
 * While `busy`, the button keeps its place in the Tab order (aria-disabled, clicks ignored) instead of
 * being disabled: a disabled button drops the keyboard focus, and the person who pressed it lands
 * back at the start of the page. Callers can do the same with `aria-disabled` for a button that
 * turns unusable right after being pressed ("Review changes" once they're saved).
 */
export function Button({ variant = 'primary', className = '', busy = false, children, onClick, ...props }) {
  const t = useT();
  const inert = busy || props['aria-disabled'] === true || props['aria-disabled'] === 'true';
  return (
    <button
      type="button"
      className={`inline-flex items-center justify-center gap-2 rounded-md px-3.5 py-2 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 aria-disabled:cursor-not-allowed aria-disabled:opacity-50 ${buttonStyles[variant]} ${className}`}
      {...props}
      aria-disabled={inert || undefined}
      aria-busy={busy || undefined}
      onClick={inert ? (e) => e.preventDefault() : onClick}
    >
      {busy ? t('common.working') : children}
    </button>
  );
}

export function ButtonLink({ to, variant = 'primary', children }) {
  return (
    <Link
      to={to}
      className={`inline-flex items-center rounded-md px-3.5 py-2 text-sm font-semibold transition-colors ${buttonStyles[variant]}`}
    >
      {children}
    </Link>
  );
}

/**
 * Label + control + help/error text, wired together for screen readers. `help` is one short line
 * under the control (≤ 120 chars; longer warns in dev); `tip` is longer help behind a "?" next to
 * the label. `hint` sits right of the label in muted text ("optional", "MB").
 */
export function Field({ label, help, tip, hint, error, children, className = '' }) {
  const id = useId();
  warnLong(`Field "${typeof label === 'string' ? label : '…'}" help`, help, HELP_MAX);
  const describedBy = error ? `${id}-error` : help ? `${id}-help` : undefined;
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <div className="flex items-center gap-1.5">
        <label id={`${id}-label`} htmlFor={id} className="text-sm font-semibold">
          {label}
        </label>
        {hint ? <span className="text-xs text-muted">{hint}</span> : null}
        {tip ? <HelpTip describedBy={`${id}-label`}>{tip}</HelpTip> : null}
      </div>
      {children({ id, 'aria-describedby': describedBy, 'aria-invalid': error ? true : undefined })}
      {error ? (
        <p id={`${id}-error`} className="text-sm text-danger">
          {error}
        </p>
      ) : help ? (
        <p id={`${id}-help`} className="text-xs text-muted">
          {help}
        </p>
      ) : null}
    </div>
  );
}

const controlBase =
  'rounded-md border border-line-strong bg-surface px-3 py-2 text-sm text-text placeholder:text-muted focus:border-accent aria-[invalid=true]:border-danger';

/**
 * Controls fill their container unless the caller gives them a width (`w-40`): both `w-full` and the
 * caller's width in one class list would leave the result to Tailwind's CSS order (w-full won).
 */
const controlClass = (className) => `${/(^|\s)!?w-/.test(className) ? '' : 'w-full'} ${controlBase}`;

export function Input({ mono = false, className = '', ...props }) {
  return <input className={`${controlClass(className)} ${mono ? 'font-mono' : ''} ${className}`} {...props} />;
}

export function Textarea({ mono = false, className = '', ...props }) {
  return (
    <textarea
      className={`${controlClass(className)} min-h-40 ${mono ? 'font-mono text-xs' : ''} ${className}`}
      {...props}
    />
  );
}

export function Select({ className = '', children, ...props }) {
  return (
    <select className={`${controlClass(className)} ${className}`} {...props}>
      {children}
    </select>
  );
}

export function Checkbox({ label, ...props }) {
  return (
    // The box keeps its size and sits on the first line when a long label wraps.
    <label className="inline-flex items-start gap-2 text-sm">
      <input type="checkbox" className="mt-0.5 size-4 shrink-0 accent-[var(--accent)]" {...props} />
      {label}
    </label>
  );
}

/**
 * "← Parent": the way back from a detail page or a sub-view, the same everywhere. `to` makes it a
 * link, `onClick` a button. On top of a page it sits above the header (default `className`); inside
 * a toolbar row pass `className=""`.
 */
export function BackLink({ to, onClick, children, className = 'mb-2' }) {
  const look = 'inline-flex items-center gap-1.5 text-sm text-muted hover:text-text';
  const inner = (
    <>
      <ArrowLeftIcon />
      {children}
    </>
  );
  return (
    <div className={className}>
      {to ? (
        <Link to={to} className={look}>
          {inner}
        </Link>
      ) : (
        <button type="button" className={look} onClick={onClick}>
          {inner}
        </button>
      )}
    </div>
  );
}

/**
 * The page's title row: `title`, at most one short line under it (`description`, ≤ 90 chars; longer
 * warns in dev), and the primary actions on the right. Longer explanations go into `help` (a "?"
 * popover next to the title) and/or `docs` (an href: "Learn more" inside it).
 */
export function PageHeader({ title, description, actions, help, docs }) {
  const id = useId();
  const t = useT();
  warnLong(`PageHeader "${typeof title === 'string' ? title : '…'}" description`, description, LEAD_MAX);
  return (
    <header className="mb-6 flex flex-wrap items-end justify-between gap-x-4 gap-y-3">
      <div className="max-w-3xl min-w-0">
        <div className="flex items-center gap-2">
          <h1 id={`${id}-title`}>{title}</h1>
          {help || docs ? (
            <HelpTip describedBy={`${id}-title`} docs={docs}>
              {help ?? t('help.page_docs')}
            </HelpTip>
          ) : null}
        </div>
        {description ? <p className="mt-1 text-muted">{description}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </header>
  );
}

const PANEL_TONES = {
  default: 'border-line',
  danger: 'border-danger/50',
};

/**
 * A titled box. `description`: one line under the title (longer goes into `help`, a "?" next to it).
 * `actions`: buttons right of the title. `compact`: tighter padding for dense pages and side
 * columns. `tone="danger"` frames a Danger zone. `headingLevel` defaults to 2. Extra props (id,
 * aria-*) go on the <section>.
 */
export function Panel({
  title,
  description,
  help,
  actions,
  compact = false,
  tone = 'default',
  headingLevel = 2,
  children,
  className = '',
  ...rest
}) {
  const H = `h${headingLevel}`;
  const id = useId();
  const hasHead = title || description || actions;
  return (
    <section
      className={`rounded-[10px] border bg-surface ${PANEL_TONES[tone] ?? PANEL_TONES.default} ${compact ? 'p-4' : 'p-5'} ${className}`}
      {...rest}
    >
      {hasHead ? (
        <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
          <div className="min-w-0">
            {title ? (
              <div className="flex items-center gap-2">
                <H
                  id={`${id}-title`}
                  className={`text-lg leading-tight font-[650] ${tone === 'danger' ? 'text-danger' : ''}`}
                >
                  {title}
                </H>
                {help ? <HelpTip describedBy={`${id}-title`}>{help}</HelpTip> : null}
              </div>
            ) : null}
            {description ? <p className="mt-0.5 max-w-3xl text-sm text-muted">{description}</p> : null}
          </div>
          {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
        </div>
      ) : null}
      <div className={hasHead ? (compact ? 'mt-3' : 'mt-4') : ''}>{children}</div>
    </section>
  );
}

/** A Panel by another name, for sections inside a page. */
export const Section = Panel;

/**
 * The one Save row of a form (docs/dev/ui-guidelines.md "Tabs, buttons and menus": one Save per form,
 * not one per field): the submit button, optional extra controls (`children`: a ghost Cancel), and the
 * form's state after it, read out loud: "Unsaved changes" while `dirty`, `saved` text after a save.
 * `sticky` keeps it in view at the bottom of a long form inside a Panel.
 */
export function SaveBar({
  busy = false,
  dirty = false,
  saved = false,
  label = 'Save',
  savedText = 'Saved.',
  dirtyText = 'Unsaved changes',
  sticky = false,
  disabled,
  children,
}) {
  return (
    <div
      className={`flex flex-wrap items-center gap-3 ${sticky ? 'sticky bottom-0 z-10 -mx-5 -mb-5 mt-1 rounded-b-[10px] border-t border-line bg-surface px-5 py-3' : ''}`}
    >
      <Button type="submit" busy={busy} disabled={disabled}>
        {label}
      </Button>
      {children}
      <span role="status" className={`text-sm ${dirty ? 'text-amber' : 'text-muted'}`}>
        {dirty ? dirtyText : saved ? savedText : ''}
      </span>
    </div>
  );
}

/** The red-framed "Danger zone" panel for destructive actions (delete, reinstall, wipe). */
export function DangerZone({ title = 'Danger zone', children, ...rest }) {
  return (
    <Panel title={title} tone="danger" {...rest}>
      <div className="flex flex-col gap-3">{children}</div>
    </Panel>
  );
}

/** One destructive action in a DangerZone: name + one line on the left, the button on the right. */
export function DangerAction({ title, description, action }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line pb-3 last:border-0 last:pb-0">
      <div className="min-w-0">
        <p className="text-sm font-semibold">{title}</p>
        {description ? <p className="text-sm text-muted">{description}</p> : null}
      </div>
      {action}
    </div>
  );
}

/**
 * Data table. `columns`: [{ key, label, render?, mono?, align?, nowrap?, className? }]; `className` goes
 * on the column's header and cells (e.g. `hidden md:table-cell` for a column phones fold into another).
 */
/** The next `?sort=` value when a sortable header is clicked: ascending first, then descending. */
export const nextSort = (current, field) => (current === field ? `-${field}` : field);

/** Header cell content: a button for sortable columns (`column.sort`: the API field, or true for its key). */
function HeaderCell({ column, sort, onSort }) {
  const field = column.sort === true ? column.key : column.sort;
  // A column without a visible title (row actions, a marker) still gets one for screen readers.
  if (!column.label && column.label !== 0) {
    const name = column.srLabel ?? column.key.replace(/[_-]+/g, ' ').replace(/^./, (c) => c.toUpperCase());
    return <span className="sr-only">{name}</span>;
  }
  if (!field || !onSort) return column.label;
  const dir = sort === field ? 'asc' : sort === `-${field}` ? 'desc' : null;
  return (
    <button type="button" className="inline-flex items-center gap-1 hover:text-text" onClick={() => onSort(field)}>
      {column.label}
      <span aria-hidden="true" className={dir ? 'text-text' : 'opacity-30'}>
        {dir === 'desc' ? <ArrowDownIcon /> : <ArrowUpIcon />}
      </span>
    </button>
  );
}

/**
 * A table. Optional sorting: columns with `sort` get a clickable header; `sort` is the current
 * value (`name` or `-name`) and `onSort(field)` is called with the clicked column's field.
 */
/**
 * `minWidth` (e.g. '36rem') keeps a many-column table from squeezing its columns into word-per-line
 * stacks on a phone: below that width it scrolls sideways instead.
 */
/**
 * The header box of a selectable table: ticks every row on the page (or clears them), and shows
 * "some" (indeterminate) when only part of the page is ticked.
 */
function SelectAllBox({ checked, some, onChange }) {
  const t = useT();
  return (
    <input
      type="checkbox"
      aria-label={t('table.select_all')}
      className="size-4 accent-[var(--accent)]"
      ref={(el) => {
        if (el) el.indeterminate = some && !checked;
      }}
      checked={checked}
      onChange={onChange}
    />
  );
}

/**
 * A data table. `selection` = `{ selected: [keys], onChange(nextKeys), label(row) }` adds a first
 * column of checkboxes (plain inputs: Tab and Space work) with "select all on this page" in the
 * header; `label` names each row's box for screen readers. `caption` names the table (screen readers
 * announce it on entering); it is visually hidden unless `showCaption` is set. `stickyHeader` keeps
 * the column titles in view: the table scrolls inside its own box (at most the window's height).
 */
export function Table({
  columns,
  rows,
  rowKey = 'uuid',
  empty,
  sort,
  onSort,
  minWidth,
  selection,
  caption,
  showCaption = false,
  stickyHeader = false,
}) {
  const t = useT();
  if (!rows?.length) return empty ?? null;
  if (selection) {
    const chosen = new Set(selection.selected);
    const keys = rows.map((r) => r[rowKey]);
    const ticked = keys.filter((k) => chosen.has(k)).length;
    const all = ticked === keys.length;
    const toggleAll = () =>
      selection.onChange(
        all ? selection.selected.filter((k) => !keys.includes(k)) : [...new Set([...selection.selected, ...keys])],
      );
    columns = [
      {
        key: '__select',
        label: <SelectAllBox checked={all} some={ticked > 0} onChange={toggleAll} />,
        render: (row) => (
          <input
            type="checkbox"
            aria-label={t('table.select_row', { name: selection.label ? selection.label(row) : row[rowKey] })}
            className="size-4 accent-[var(--accent)]"
            checked={chosen.has(row[rowKey])}
            onChange={() =>
              selection.onChange(
                chosen.has(row[rowKey])
                  ? selection.selected.filter((k) => k !== row[rowKey])
                  : [...selection.selected, row[rowKey]],
              )
            }
          />
        ),
        width: 'w-10',
      },
      ...columns,
    ];
  }
  const isSelected = (row) => Boolean(selection?.selected.includes(row[rowKey]));
  return (
    // `relative`: screen-reader-only labels in the cells are positioned against the scroll box, so
    // they scroll with it instead of widening the page.
    <div
      className={`relative overflow-x-auto rounded-[10px] border border-line bg-surface ${stickyHeader ? 'max-h-[calc(100dvh-10rem)] overflow-y-auto' : ''}`}
    >
      <table className="w-full border-collapse text-sm" style={minWidth ? { minWidth } : undefined}>
        {caption ? (
          <caption className={showCaption ? 'px-4 pt-3 pb-1 text-left font-semibold' : 'sr-only'}>{caption}</caption>
        ) : null}
        <thead>
          <tr className="border-b border-line text-left text-muted">
            {columns.map((c) => (
              <th
                key={c.key}
                scope="col"
                className={`px-4 py-2.5 font-semibold ${stickyHeader ? 'sticky top-0 z-[1] bg-surface shadow-[inset_0_-1px_0_var(--line)]' : ''} ${c.align === 'right' ? 'text-right' : ''} ${c.nowrap ? 'whitespace-nowrap' : ''} ${c.width ?? ''} ${c.className ?? ''}`}
                aria-sort={
                  onSort && c.sort
                    ? sort === (c.sort === true ? c.key : c.sort)
                      ? 'ascending'
                      : sort === `-${c.sort === true ? c.key : c.sort}`
                        ? 'descending'
                        : 'none'
                    : undefined
                }
              >
                <HeaderCell column={c} sort={sort} onSort={onSort} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row[rowKey]}
              className={`border-b border-line last:border-0 hover:bg-sunk ${isSelected(row) ? 'bg-accent/5' : ''}`}
            >
              {columns.map((c) => (
                <td
                  key={c.key}
                  className={`px-4 py-2.5 align-middle ${c.mono ? 'font-mono text-xs leading-5' : ''} ${c.align === 'right' ? 'text-right' : ''} ${c.nowrap ? 'whitespace-nowrap' : ''} ${c.className ?? ''}`}
                >
                  {c.render ? c.render(row) : (row[c.key] ?? '')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const STATUS = {
  installing: ['bg-amber', 'installing'],
  install_failed: ['bg-danger', 'install_failed'],
  suspended: ['bg-danger', 'suspended'],
  null: ['bg-kelp', 'ready'],
};
// Once installed, what the node reports says more than "ready".
const POWER = {
  running: ['bg-kelp', 'running'],
  starting: ['bg-amber', 'starting'],
  stopping: ['bg-amber', 'stopping'],
  offline: ['bg-muted', 'offline'],
  sleeping: ['bg-accent', 'sleeping'],
};
const statusOf = (status, power) => (status == null && POWER[power]) || STATUS[status] || null;

/**
 * The word ServerStatus shows ("Running", "Install failed", …), for places with their own dot. `t`:
 * the catalogue's t() (the admin's wording); English without it. An unknown status shows as is.
 */
export const serverStatusLabel = (status, power, t = makeT()) => {
  const known = statusOf(status, power);
  return known ? t(`server_status.${known[1]}`) : status;
};

export function ServerStatus({ status, power }) {
  const t = useT();
  const dot = statusOf(status, power)?.[0] ?? 'bg-muted';
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap">
      <span className={`size-2 rounded-full ${dot}`} aria-hidden="true" />
      {serverStatusLabel(status, power, t)}
    </span>
  );
}

/**
 * Nothing to show yet: `title` (one line), optional `children` (one more short line), one `action`.
 * `icon`: a short glyph or element above the title. `page` makes the title the page's heading (a
 * whole page that is only this message); `compact` for an empty list inside a panel.
 */
export function EmptyState({ title, children, action, icon, page = false, compact = false }) {
  const Title = page ? 'h1' : 'p';
  return (
    <div
      className={`rounded-[10px] border border-dashed border-line bg-surface/50 text-center ${compact ? 'px-4 py-6' : 'px-6 py-10'}`}
    >
      {icon ? (
        <div aria-hidden="true" className="mb-2 text-2xl text-muted">
          {icon}
        </div>
      ) : null}
      <Title className="text-base font-semibold">{title}</Title>
      {children ? <p className="mx-auto mt-1 max-w-md text-sm text-muted">{children}</p> : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

/** Retrying helps after a network error, a 5xx, a timeout or a rate limit; not after a 403 or a 404. */
const retryable = (error) => !error.status || error.status >= 500 || error.status === 408 || error.status === 429;

/**
 * Shows an API error (or any error) in the interface's voice. When the error is a failed query's
 * (a page that could not load), it offers "Try again", which refetches the queries that failed with
 * it, so no caller has to wire that up; `onRetry` sets the action by hand (or `false` hides it).
 */
export function ErrorNote({ error, onRetry }) {
  const client = useContext(QueryClientContext);
  const t = useT();
  const [retrying, setRetrying] = useState(false);
  if (!error || error.silent) return null;
  const failedWith = (q) => q.state.error === error;
  const retry =
    onRetry === false
      ? null
      : (onRetry ??
        (client && retryable(error) && client.getQueryCache().findAll({ predicate: failedWith }).length
          ? () => client.refetchQueries({ predicate: failedWith })
          : null));
  return (
    <div role="alert" className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
      {retry ? (
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
          <span>{error.message}</span>
          <button
            type="button"
            aria-disabled={retrying || undefined}
            className="rounded-md px-2 py-0.5 font-semibold text-danger underline-offset-2 hover:underline aria-disabled:opacity-60"
            onClick={async () => {
              if (retrying) return;
              setRetrying(true);
              try {
                await retry();
              } finally {
                setRetrying(false);
              }
            }}
          >
            {retrying ? t('common.retrying') : t('common.retry')}
          </button>
        </div>
      ) : (
        error.message
      )}
      {error.details?.issues?.length ? (
        <ul className="mt-1 list-disc pl-5">
          {error.details.issues.map((i) => (
            <li key={i}>{i}</li>
          ))}
        </ul>
      ) : null}
      {error.details?.errors?.length ? (
        <ul className="mt-1 list-disc pl-5">
          {error.details.errors.map((e) =>
            // Rule checks (startup variables) answer plain strings; validation answers { field, message }.
            typeof e === 'string' ? (
              <li key={e}>{e}</li>
            ) : (
              <li key={e.field}>
                <span className="font-mono text-xs">{e.field}</span>: {e.message}
              </li>
            ),
          )}
        </ul>
      ) : null}
    </div>
  );
}

/** The older plain notice box; new code uses InlineNotice (icon, `danger` tone, details). */
export function Notice({ tone = 'info', children }) {
  const tones = {
    info: 'border-line bg-sunk text-text',
    warn: 'border-amber/50 bg-amber/10 text-text',
    ok: 'border-kelp/50 bg-kelp/10 text-text',
    danger: 'border-danger/50 bg-danger/10 text-text',
  };
  return <div className={`rounded-md border px-3 py-2 text-sm ${tones[tone]}`}>{children}</div>;
}

/**
 * Something is on its way. With a `label` ("Loading payment options") it is that line of text;
 * without one it is a skeleton: grey bars in the shape of the content, read out as "Loading…".
 * `rows` sets how many bars (3); `inline` keeps the plain line for a spot inside a sentence or row.
 */
export function Loading({ label, rows = 3, inline = false }) {
  const t = useT();
  if (label || inline) {
    return (
      <p className="text-sm text-muted" role="status">
        {label ? `${label}…` : t('common.loading')}
      </p>
    );
  }
  return (
    <div role="status" className="flex flex-col gap-3 py-1" data-skeleton="">
      <span className="sr-only">{t('common.loading')}</span>
      {Array.from({ length: rows }, (_, i) => (
        <span
          key={i}
          aria-hidden="true"
          className={`block h-3.5 animate-pulse rounded bg-line/80 ${SKELETON_WIDTHS[i % SKELETON_WIDTHS.length]}`}
        />
      ))}
    </div>
  );
}

const SKELETON_WIDTHS = ['w-1/3', 'w-11/12', 'w-3/4', 'w-5/6', 'w-2/3'];

/** A placeholder card while a panel's data loads: the panel's frame with skeleton bars inside. */
export function SkeletonPanel({ rows = 3, className = '' }) {
  return (
    <div className={`rounded-[10px] border border-line bg-surface p-5 ${className}`}>
      <Loading rows={rows} />
    </div>
  );
}

export const mb = (v) => (v >= 1024 ? `${(v / 1024).toFixed(v % 1024 ? 1 : 0)} GB` : `${v} MB`);
